import type {
  WeaponCase,
  CaseOpenResult,
  CaseDropPool,
  CaseDropEntry,
  UnboxPhase,
} from './weapon-case-types';
import { RARITY_DROP_RATES, STATTRAK_CHANCE, RARITY_INFO } from './weapon-case-types';
import type { SkinRarity } from './weapon-skin-types';
import { wearLevelFromFloat, wearLevelDisplayName } from './weapon-skin-types';
import { weaponSkinRegistry } from './weapon-skin-registry';

// ---------------------------------------------------------------------------
// Case Opening Animation Controller
// ---------------------------------------------------------------------------
// F-CM-009 (2026-05-10): the CaseOpener class below has no production callers
// yet. docs/20marskins.md:1779 lists the case-opening flow under "P2 — Economy"
// and marks it complete, but the UI panel that drives this controller (an
// inventory-side "Open Case" button + a dedicated unbox screen) was never
// wired up — only the underlying types, drop pools, server endpoint, and this
// animation/state machine. The controller is intentionally retained so the
// future UI work is a wiring task rather than a re-implementation. When the
// case-opening UI is built, mount this class on the inventory panel, drive
// `update(dt)` from the inventory render loop, and surface the result via
// `setCallbacks(onPhaseChange, onComplete)`.
// ---------------------------------------------------------------------------

/** Reel item displayed during scrolling animation. */
export interface ReelItem {
  skinId: number;
  name: string;
  weaponId: string;
  rarity: SkinRarity;
  thumbnailPath: string;
}

/** Animation timing constants. */
const REEL_ITEM_COUNT = 40;        // Total items in the reel
const REEL_SCROLL_DURATION = 3.0;  // Seconds of scrolling
const REEL_DECEL_DURATION = 2.0;   // Seconds of deceleration
const REVEAL_DELAY = 0.5;          // Pause before reveal effects
const REVEAL_DURATION = 2.0;       // Reveal effect duration

/**
 * Case opening animation and logic controller.
 *
 * The actual random roll happens SERVER-SIDE. This class handles:
 * 1. Building the visual reel (fake items + the real result)
 * 2. Animating the reel scroll and deceleration
 * 3. Triggering reveal effects (color flash, sound cue)
 * 4. Displaying the won item details
 *
 * Usage:
 *   const opener = new CaseOpener();
 *   opener.startOpen(caseData, serverResult);
 *   // In render loop:
 *   opener.update(dt);
 *   const state = opener.getState();
 */
export class CaseOpener {
  private phase: UnboxPhase = 'idle';
  private timer = 0;
  private activeCase: WeaponCase | null = null;
  private result: CaseOpenResult | null = null;

  // Reel state
  private reelItems: ReelItem[] = [];
  private reelPosition = 0;       // Current scroll position (0 = start)
  private reelTargetIndex = 0;    // Index of winning item in reel
  private reelVelocity = 0;       // Current scroll velocity
  private readonly itemWidth = 120; // Pixels per reel item

  // Callbacks
  private onPhaseChange: ((phase: UnboxPhase) => void) | null = null;
  private onComplete: ((result: CaseOpenResult) => void) | null = null;

  get currentPhase(): UnboxPhase { return this.phase; }
  get currentResult(): CaseOpenResult | null { return this.result; }
  get currentReelItems(): readonly ReelItem[] { return this.reelItems; }
  get currentReelPosition(): number { return this.reelPosition; }

  /**
   * Register callbacks for animation events.
   */
  setCallbacks(
    onPhaseChange?: (phase: UnboxPhase) => void,
    onComplete?: (result: CaseOpenResult) => void,
  ): void {
    this.onPhaseChange = onPhaseChange ?? null;
    this.onComplete = onComplete ?? null;
  }

  /**
   * Start the case opening animation.
   *
   * @param weaponCase - The case being opened
   * @param serverResult - The result from the server (determines what's won)
   */
  startOpen(weaponCase: WeaponCase, serverResult: CaseOpenResult): void {
    this.activeCase = weaponCase;
    this.result = serverResult;
    this.timer = 0;

    // Build the visual reel with the winning item embedded
    this.reelItems = this.buildReel(weaponCase, serverResult);
    this.reelTargetIndex = Math.floor(REEL_ITEM_COUNT * 0.75); // Win item at 75% through reel
    this.reelPosition = 0;
    this.reelVelocity = this.itemWidth * 15; // Initial speed: 15 items per second

    this.setPhase('opening');
  }

  /**
   * Update the animation state. Call every frame.
   */
  update(dt: number): void {
    this.timer += dt;

    switch (this.phase) {
      case 'opening':
        // Brief pause for case-open animation (0.5s)
        if (this.timer >= 0.5) {
          this.timer = 0;
          this.setPhase('scrolling');
        }
        break;

      case 'scrolling':
        // Fast scroll at constant velocity
        this.reelPosition += this.reelVelocity * dt;
        if (this.timer >= REEL_SCROLL_DURATION) {
          this.timer = 0;
          this.setPhase('decelerating');
        }
        break;

      case 'decelerating': {
        // Decelerate towards the target position
        const targetPos = this.reelTargetIndex * this.itemWidth;
        const remaining = targetPos - this.reelPosition;

        if (remaining <= 0 || this.timer >= REEL_DECEL_DURATION) {
          // Snap to target
          this.reelPosition = targetPos;
          this.reelVelocity = 0;
          this.timer = 0;
          this.setPhase('reveal');
        } else {
          // Ease-out deceleration
          const t = Math.min(1, this.timer / REEL_DECEL_DURATION);
          const easeOut = 1 - (1 - t) * (1 - t); // quadratic ease-out
          this.reelVelocity = this.itemWidth * 15 * (1 - easeOut);
          this.reelPosition += this.reelVelocity * dt;

          // Don't overshoot
          if (this.reelPosition >= targetPos) {
            this.reelPosition = targetPos;
          }
        }
        break;
      }

      case 'reveal':
        // Pause for dramatic effect, then show result
        if (this.timer >= REVEAL_DELAY + REVEAL_DURATION) {
          this.setPhase('complete');
          if (this.result && this.onComplete) {
            this.onComplete(this.result);
          }
        }
        break;

      case 'complete':
      case 'idle':
        // No animation
        break;
    }
  }

  /**
   * Get the current animation state for rendering.
   */
  getState(): {
    phase: UnboxPhase;
    timer: number;
    reelPosition: number;
    reelItems: readonly ReelItem[];
    result: CaseOpenResult | null;
    revealProgress: number;
    rarityColor: string;
    rarityGlow: string;
    rarityLabel: string;
  } {
    const rarity = this.result?.rarity ?? 'consumer';
    const info = RARITY_INFO[rarity];

    let revealProgress = 0;
    if (this.phase === 'reveal') {
      revealProgress = Math.min(1, (this.timer - REVEAL_DELAY) / REVEAL_DURATION);
    } else if (this.phase === 'complete') {
      revealProgress = 1;
    }

    return {
      phase: this.phase,
      timer: this.timer,
      reelPosition: this.reelPosition,
      reelItems: this.reelItems,
      result: this.result,
      revealProgress,
      rarityColor: info.color,
      rarityGlow: info.glowColor,
      rarityLabel: info.label,
    };
  }

  /**
   * Get display info for the won item.
   */
  getResultDisplay(): {
    name: string;
    weapon: string;
    rarity: string;
    wear: string;
    wearFloat: string;
    isStatTrak: boolean;
    color: string;
  } | null {
    if (!this.result) return null;

    const rarity = this.result.rarity;
    const info = RARITY_INFO[rarity];
    const wearLevel = wearLevelFromFloat(this.result.wearFloat);

    return {
      name: this.result.skinName,
      weapon: this.result.weaponId.toUpperCase(),
      rarity: info.label,
      wear: wearLevelDisplayName(wearLevel),
      wearFloat: this.result.wearFloat.toFixed(8),
      isStatTrak: this.result.isStatTrak,
      color: info.color,
    };
  }

  /**
   * Reset the opener to idle state.
   */
  reset(): void {
    this.phase = 'idle';
    this.timer = 0;
    this.activeCase = null;
    this.result = null;
    this.reelItems = [];
    this.reelPosition = 0;
    this.reelVelocity = 0;
  }

  // ---------------------------------------------------------------------------
  // Reel Building
  // ---------------------------------------------------------------------------

  /**
   * Build the visual reel for the scrolling animation.
   * Places random skins from the case pool, with the winning item
   * at a predetermined position.
   */
  private buildReel(weaponCase: WeaponCase, result: CaseOpenResult): ReelItem[] {
    const items: ReelItem[] = [];
    const winIndex = this.reelTargetIndex;

    for (let i = 0; i < REEL_ITEM_COUNT; i++) {
      if (i === winIndex) {
        // Place the winning item
        const skin = weaponSkinRegistry.getSkin(result.skinId);
        items.push({
          skinId: result.skinId,
          name: result.skinName,
          weaponId: result.weaponId,
          rarity: result.rarity,
          thumbnailPath: skin?.thumbnailPath ?? '',
        });
      } else {
        // Random filler from case pool
        items.push(this.randomReelItem(weaponCase));
      }
    }

    return items;
  }

  /**
   * Pick a random item from the case drop pool for reel filler.
   * Uses the actual drop rates so the reel looks realistic.
   */
  private randomReelItem(weaponCase: WeaponCase): ReelItem {
    const roll = Math.random();
    let cumulative = 0;

    const tierOrder: (keyof CaseDropPool)[] = [
      'consumer', 'industrial', 'mil-spec', 'restricted', 'classified', 'covert',
    ];

    let selectedTier: keyof CaseDropPool = 'consumer';
    for (const tier of tierOrder) {
      cumulative += RARITY_DROP_RATES[tier];
      if (roll < cumulative) {
        selectedTier = tier;
        break;
      }
    }

    const pool = weaponCase.dropPool[selectedTier];
    if (pool.length === 0) {
      // Fallback to consumer if tier is empty
      return this.pickFromPool(weaponCase.dropPool.consumer, 'consumer');
    }

    return this.pickFromPool(pool, selectedTier as SkinRarity);
  }

  private pickFromPool(pool: CaseDropEntry[], rarity: SkinRarity): ReelItem {
    // Weighted random selection
    const totalWeight = pool.reduce((sum, e) => sum + e.weight, 0);
    let roll = Math.random() * totalWeight;

    for (const entry of pool) {
      roll -= entry.weight;
      if (roll <= 0) {
        const skin = weaponSkinRegistry.getSkin(entry.skinId);
        return {
          skinId: entry.skinId,
          name: skin?.name ?? 'Unknown',
          weaponId: skin?.weaponIds[0] ?? '',
          rarity,
          thumbnailPath: skin?.thumbnailPath ?? '',
        };
      }
    }

    // Fallback to first entry
    const fallback = pool[0];
    const skin = weaponSkinRegistry.getSkin(fallback.skinId);
    return {
      skinId: fallback.skinId,
      name: skin?.name ?? 'Unknown',
      weaponId: skin?.weaponIds[0] ?? '',
      rarity,
      thumbnailPath: skin?.thumbnailPath ?? '',
    };
  }

  private setPhase(phase: UnboxPhase): void {
    this.phase = phase;
    if (this.onPhaseChange) {
      this.onPhaseChange(phase);
    }
  }
}

// ---------------------------------------------------------------------------
// Server-Side Case Opening Simulation (for testing / offline mode)
// ---------------------------------------------------------------------------

/**
 * Simulate a case opening result (for testing without server).
 * In production, the server handles all randomization.
 */
export function simulateCaseOpen(weaponCase: WeaponCase): CaseOpenResult {
  // Roll for special item first
  if (Math.random() < weaponCase.specialRate && weaponCase.specialPool.length > 0) {
    const entry = weightedPick(weaponCase.specialPool);
    return buildResult(entry.skinId, 'special');
  }

  // Roll rarity tier
  const roll = Math.random();
  let cumulative = 0;
  const tierOrder: (keyof CaseDropPool)[] = [
    'covert', 'classified', 'restricted', 'mil-spec', 'industrial', 'consumer',
  ];

  // Iterate from rarest to most common
  for (const tier of tierOrder) {
    cumulative += RARITY_DROP_RATES[tier];
    if (roll < cumulative || tier === 'consumer') {
      const pool = weaponCase.dropPool[tier];
      if (pool.length > 0) {
        const entry = weightedPick(pool);
        return buildResult(entry.skinId, tier as SkinRarity);
      }
    }
  }

  // Should never reach here, but fallback
  const fallbackPool = weaponCase.dropPool.consumer;
  const entry = fallbackPool[0];
  return buildResult(entry.skinId, 'consumer');
}

function weightedPick(pool: CaseDropEntry[]): CaseDropEntry {
  const total = pool.reduce((sum, e) => sum + e.weight, 0);
  let roll = Math.random() * total;
  for (const entry of pool) {
    roll -= entry.weight;
    if (roll <= 0) return entry;
  }
  return pool[pool.length - 1];
}

function buildResult(skinId: number, rarity: SkinRarity): CaseOpenResult {
  const skin = weaponSkinRegistry.getSkin(skinId);
  const wearRange = skin?.wearRange ?? [0, 1];
  const wearFloat = wearRange[0] + Math.random() * (wearRange[1] - wearRange[0]);
  const patternSeed = skin?.pattern ? Math.floor(Math.random() * (skin.pattern.seedRange[1] - skin.pattern.seedRange[0]) + skin.pattern.seedRange[0]) : 0;
  const isStatTrak = Math.random() < STATTRAK_CHANCE;

  return {
    skinId,
    wearFloat,
    patternSeed,
    isStatTrak,
    rarity,
    instanceId: crypto.randomUUID(),
    skinName: skin?.name ?? 'Unknown',
    weaponId: skin?.weaponIds[0] ?? '',
  };
}
